import AnnouncementBannerToneEnum from '../Enums/AnnouncementBannerToneEnum';
import InvoiceAutomationRunScheduleEnum from '../Enums/InvoiceAutomationRunScheduleEnum';
import InvoiceAutomationStatusEnum from '../Enums/InvoiceAutomationStatusEnum';
import { formatProjectLabel } from './invoiceAutomationEditorHelper';

// The status badge of a project's automatic invoicing: green Active, red Deactivated, and no badge at all when the
// project has no setup (isActive is then null). An Active setup that runs only on demand says so in its label.
export function getInvoiceAutomationStatusBadge(isActive?: boolean | null, runSchedule?: string | null) {
  if (isActive == null) {
    return null;
  }
  if (!isActive) {
    return { variant: 'deactivated', label: InvoiceAutomationStatusEnum.Deactivated };
  }
  if (runSchedule === InvoiceAutomationRunScheduleEnum.OnDemand) {
    return { variant: 'active', label: `${InvoiceAutomationStatusEnum.Active} · on demand` };
  }
  return { variant: 'active', label: InvoiceAutomationStatusEnum.Active };
}

// What the status badge of a project on another setup's invoice adds: a short note naming the project whose setup it
// is, and a tooltip saying what that means.
export function getInvoicedWithNote(invoicedWithProjectId: number, invoicedWithProjectName?: string | null) {
  const invoicedWithProject = formatProjectLabel(invoicedWithProjectId, invoicedWithProjectName);
  return {
    text: `via project #${invoicedWithProjectId}`,
    tooltip: `This project is on the invoice of project ${invoicedWithProject}, whose automatic invoicing invoices its jobs.`,
  };
}

// The "Automatic" badge of an invoice the automation made, coloured by its project's setup: green while it is Active,
// red while it is Deactivated. No badge for an invoice a person made (isActive is then null).
export function getAutomaticInvoiceBadge(isActive?: boolean | null) {
  if (isActive == null) {
    return null;
  }
  if (!isActive) {
    return {
      variant: 'deactivated',
      label: 'Automatic',
      tooltip: "Made by automatic invoicing, which is now deactivated for the invoice's project.",
    };
  }
  return {
    variant: 'active',
    label: 'Automatic',
    tooltip: "Made by automatic invoicing, which is active for the invoice's project.",
  };
}

// The question before Remove on a third-party invoice. On one of the automation's drafts (Active setup or not) it adds
// that no run adds a removed job again by itself: Run Now can bring it back in, or a person adds it by hand.
export function getRemoveQuestion(question: string, invoiceAutomationIsActive?: boolean | null) {
  if (invoiceAutomationIsActive == null) {
    return question;
  }
  return (
    `${question} Automatic invoicing made this draft and will not add the removed jobs to a draft again by itself. ` +
    "To invoice them, bring them back in with Run Now, or add them by hand from the project's Ready for Invoicing list."
  );
}

// The banner over a project's Ready for Invoicing jobs: green while the setup is Active, saying how the jobs reach the
// drafts, and red while it is Deactivated; null when the project has no setup.
export function getReadyForInvoicingBanner(isActive?: boolean | null, runSchedule?: string | null) {
  if (isActive == null) {
    return null;
  }
  if (!isActive) {
    return {
      tone: AnnouncementBannerToneEnum.Critical,
      title: 'Automatic invoicing is deactivated',
      text: "Bravo leaves these jobs alone until the project's automatic invoicing is activated.",
    };
  }
  if (runSchedule === InvoiceAutomationRunScheduleEnum.OnDemand) {
    return {
      tone: AnnouncementBannerToneEnum.Info,
      title: 'Automatic invoicing is active, on demand',
      text: "When someone clicks Run Now, Bravo adds the ready jobs from the setup's first month on to its draft invoices.",
    };
  }
  return {
    tone: AnnouncementBannerToneEnum.Info,
    title: 'Automatic invoicing is active',
    text:
      "Every morning, Bravo adds the ready jobs from the setup's first month on to its draft invoices automatically; " +
      'nobody needs to click anything. Run Now adds them straight away.',
  };
}
